// ─── Date & time formatting utilities ───────────────────────────────────────
// Central place for all date rendering so a single settings flip (dateFormat)
// propagates to every date shown in the app. Time is always 24h.

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG  = ["January","February","March","April","May","June","July","August","September","October","November","December"];

/**
 * Supported date-format tokens — keep stable; stored in _appSettings.dateFormat
 *   "DD/MM/YYYY" → 14/04/2026   (EU default)
 *   "MM/DD/YYYY" → 04/14/2026   (US)
 *   "YYYY-MM-DD" → 2026-04-14   (ISO)
 *   "D MMM YYYY" → 14 Apr 2026  (human)
 */
export const DATE_FORMATS = ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", "D MMM YYYY"];
export const DEFAULT_DATE_FORMAT = "DD/MM/YYYY";

/** Coerce anything to a Date — ISO string, Date, number, or null. Returns null on failure. */
function toDate(d) {
  if (d == null || d === "") return null;
  if (d instanceof Date) return isNaN(d) ? null : d;
  const dt = new Date(d);
  return isNaN(dt) ? null : dt;
}

/** Format a date using the provided format token. Falls back to default if unknown. */
export function formatDate(d, format = DEFAULT_DATE_FORMAT) {
  const dt = toDate(d);
  if (!dt) return "";
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  const mShort = MONTHS_SHORT[dt.getMonth()];
  switch (format) {
    case "MM/DD/YYYY": return `${mm}/${dd}/${yyyy}`;
    case "YYYY-MM-DD": return `${yyyy}-${mm}-${dd}`;
    case "D MMM YYYY": return `${dt.getDate()} ${mShort} ${yyyy}`;
    case "DD/MM/YYYY":
    default:           return `${dd}/${mm}/${yyyy}`;
  }
}

/** Always 24h, HH:MM. */
export function formatTime(d) {
  const dt = toDate(d);
  if (!dt) return "";
  return `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
}

/** Date + time, space-separated, using the selected date format + 24h time. */
export function formatDateTime(d, format = DEFAULT_DATE_FORMAT) {
  const date = formatDate(d, format);
  const time = formatTime(d);
  return date && time ? `${date} ${time}` : date || "";
}

/** Short month label: "Apr 2026". Not format-dependent; used for chart axes. */
export function formatMonthYear(d) {
  const dt = toDate(d);
  if (!dt) return "";
  return `${MONTHS_SHORT[dt.getMonth()]} ${dt.getFullYear()}`;
}

/** Long month label: "April 2026". */
export function formatMonthYearLong(d) {
  const dt = toDate(d);
  if (!dt) return "";
  return `${MONTHS_LONG[dt.getMonth()]} ${dt.getFullYear()}`;
}

/** Read date format from _appSettings with fallback. Pass months._appSettings or nothing. */
export function getDateFormat(appSettings) {
  const f = appSettings?.dateFormat;
  return DATE_FORMATS.includes(f) ? f : DEFAULT_DATE_FORMAT;
}
